/**
 * @fileoverview The Control Panel's own top bar.
 *
 * THE CONTROL PANEL OWNS ITS CHROME. This route rendered the app's generic
 * `ModuleHeader` — which carries a centred `Search… Ctrl+K` box that has no
 * counterpart on the tenant — and I then hung the scope picker underneath as a
 * second row, which pushed the sidebar and the whole content column down. Two
 * stacked bars where the reference has one, and it was the first thing anyone
 * saw.
 *
 * The captured structure (fidelity/references/tenant/home) is one bar, and it
 * sits INSIDE the banner wrapper as a sibling of the classification banner:
 *
 *   div.control-panel-shell__header__t7drfl
 *     div.control-panel-shell__title__t7drfl
 *       div.control-panel-shell__cp-icon__t7drfl > span (the app glyph, 50×50)
 *       "Control Panel"
 *     div.bp6-divider.control-panel-shell__divider__t7drfl
 *     div.control-panel-shell__new-feature-wrapper__t7drfl
 *       div.control-panel-shell__picker__t7drfl .walkthrough-picker-container
 *         enrollment · slash · organization · divider · space
 *
 * PICKERS ARE DISABLED, DELIBERATELY. Upstream they are `Select`s. This
 * deployment has one enrollment and the agency IS the tenant boundary, so a
 * dropdown would offer exactly one choice — a control that teaches the reader
 * their input does not matter. They render as static labels with the same
 * classes, which is what the tenant shows too for its single-item org picker
 * (`__single-item__1k9nael`, `disabled`).
 *
 * NO SPACE PICKER. Upstream's third segment selects a Space, and this product
 * has no space concept. The segment and its divider are omitted rather than
 * stubbed: a breadcrumb ending in a control that configures nothing is worse
 * than a shorter breadcrumb.
 */

import { Button, Divider } from "@blueprintjs/core";

import {
  controlPanelShell as fdy,
  resourceIcon as ri,
} from "../../../../components/control-panel/foundry-parts.generated";

export interface ControlPanelHeaderProps {
  /** The enrollment — this deployment. */
  readonly enrollmentName: string;
  /** The organization — the agency, which is our tenant boundary. */
  readonly organizationName: string;
}

export function ControlPanelHeader({
  enrollmentName,
  organizationName,
}: ControlPanelHeaderProps): React.ReactElement {
  return (
    <div className={fdy["header@t7drfl"]} data-testid="control-panel-header">
      <div className={fdy["title@t7drfl"]}>
        {/*
          AN EMPTY SPAN, NOT AN ICON COMPONENT. `cp-icon` is a 50x50 box and the
          glyph inside it is a CSS SPRITE — the span has no children upstream.
          A Blueprint <Icon> emits `span > svg`, and that extra svg is where the
          DOM walk diverged at 112 nodes of 356.

          I had said this namespace was not vendored and substituted a cog. It
          was simply missing from the surface's namespace allowlist in
          sync-foundry-surface.mjs; `resource-icon` is in the parts map now.
        */}
        <div className={fdy["cp-icon"]}>
          <span
            className={ri["app-icon-view"]}
            style={{ height: 50, width: 50 }}
            aria-hidden="true"
          />
        </div>
        Control Panel
      </div>
      <Divider className={fdy["divider"]} />
      <div className={fdy["new-feature-wrapper"]}>
        <div className={fdy["picker"]}>
          <Button
            alignText="left"
            className={fdy["picker-wrapper"]}
            disabled
            icon="diagram-tree"
            minimal
          >
            <span className={`${fdy["enrollment-picker-name"]} bp6-text-overflow-ellipsis`}>
              {enrollmentName}
            </span>
          </Button>
          {/*
            The separator is an inline SVG upstream — a 6×18 stroked line, not a
            slash character — so that it keeps its weight independent of the
            font. Reproduced rather than substituted with "/", which renders at
            whatever the label font does and sits on the text baseline.
          */}
          <span className={fdy["slash"]} aria-hidden="true">
            <svg width="6" height="18" viewBox="0 0 6 18" fill="none">
              <path d="M5.08984 1.26575L1.00032 16.7343" stroke="#ABB3BF" strokeLinecap="round" />
            </svg>
          </span>
          <div className={fdy["container@1k9nael"]}>
            <Button
              alignText="left"
              /*
                `single-item` and `active-button@1k9nael` together, as the
                capture has both: the org button is the active one AND the only
                one, and the two classes style different things.
              */
              className={`${fdy["active-button@1k9nael"]} ${fdy["single-item"]}`}
              disabled
              fill
              icon="office"
              minimal
            >
              <strong className="bp6-text-overflow-ellipsis">{organizationName}</strong>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
